import React, { FC, useEffect } from "react";
import clsx from "clsx";
import { format } from "date-fns";

import { IActionLessons } from "../../../../state";
import { ReactComponent as CheckIconCircleOn } from "../../../../assets/images/ActionList/icon_check_circle_on.svg";
import { ReactComponent as CheckIconCircleOff } from "../../../../assets/images/ActionList/icon_check_circle_off.svg";

import styles from "./ActionAnswerForm.module.scss";

export type Props = {
  lesson: IActionLessons;
  handleSubmitRealization: (
    lesson_id: number,
    content: string,
    realization_id: number | null,
  ) => void;
  calling: boolean;
  isRetrospectiveAnswered: (lesson: IActionLessons) => boolean;
  handleActionAnswerDirty: (lessonId: number, isDirty: boolean) => void;
};

const NO_RETROSPECTIVE_MESSAGE = "まだ振り返りWORKを入力していません。";

const ActionAnswerForm: FC<Props> = ({
  lesson,
  handleSubmitRealization,
  calling,
  isRetrospectiveAnswered,
  handleActionAnswerDirty,
}) => {
  const [content, setContent] = React.useState<string>(
    lesson.reflection_realization_reference?.content ?? "",
  );

  const savedContent = lesson.reflection_realization_reference?.content ?? "";
  const isDirtyContent = savedContent !== content;

  useEffect(() => {
    handleActionAnswerDirty(lesson.id, isDirtyContent);
  }, [lesson.id, isDirtyContent, handleActionAnswerDirty]);

  useEffect(() => {
    if (lesson.reflection_realization_reference !== null) {
      setContent(lesson.reflection_realization_reference.content);
    }
  }, [lesson.reflection_realization_reference]);

  const isReflected = lesson?.reflection_realization_reference !== null;

  return (
    <div className={clsx(styles.form, isReflected && styles.isReflected)}>
      <div className={styles.inner}>
        <div className={styles.header}>
          {isReflected ? (
            <CheckIconCircleOn className={styles.checkIcon} />
          ) : (
            <CheckIconCircleOff className={styles.checkIcon} />
          )}
          <span className={styles.name}>{lesson.name}</span>
          <span className={styles.title}>{lesson.title}</span>
        </div>
        {isRetrospectiveAnswered(lesson) || isReflected ? (
          <div className={styles.content}>
            <div className={styles.retrospective}>
              <div className={styles.item}>
                <p className={styles.heading}>
                  <span className={styles.number}>01</span>
                  <span className={styles.main}>授業で学んだこと</span>
                  <span className={styles.sub}>振り返りWORKに入力した内容</span>
                </p>
                <p
                  className={clsx(
                    styles.answer,
                    !lesson?.answer?.contents?.[0]?.content && styles.noMessage,
                  )}
                >
                  {lesson?.answer?.contents?.[0]?.content ||
                    NO_RETROSPECTIVE_MESSAGE}
                </p>
                {lesson?.answer?.contents?.[0]?.content &&
                  lesson?.answer?.updated_at && (
                    <p className={styles.time}>
                      {format(
                        new Date(lesson.answer.updated_at),
                        "yyyy/MM/dd HH:mm",
                      )}
                    </p>
                  )}
              </div>
              <div className={styles.item}>
                <p className={styles.heading}>
                  <span className={styles.number}>02</span>
                  <span className={styles.main}>
                    学びから行動するとしたら？
                  </span>
                  <span className={styles.sub}>振り返りWORKに入力した内容</span>
                </p>
                <p
                  className={clsx(
                    styles.answer,
                    !lesson?.answer?.contents?.[1]?.content && styles.noMessage,
                  )}
                >
                  {lesson?.answer?.contents?.[1]?.content ||
                    NO_RETROSPECTIVE_MESSAGE}
                </p>
                {lesson?.answer?.contents?.[1]?.content &&
                  lesson?.answer?.updated_at && (
                    <p className={styles.time}>
                      {format(
                        new Date(lesson.answer.updated_at),
                        "yyyy/MM/dd HH:mm",
                      )}
                    </p>
                  )}
              </div>
            </div>
            <div className={styles.reflection}>
              <div className={styles.item}>
                <p className={styles.heading}>
                  <span className={styles.number}>03</span>
                  <span className={styles.main}>行動した内容と気づき</span>
                </p>
                <textarea
                  id={`reflection-${lesson.id}`}
                  name={`reflection-${lesson.id}`}
                  className={styles.textarea}
                  placeholder="具体的にどんな行動をしたのか、それによって気づいたことや思ったことなどを入力しましょう。"
                  value={content}
                  onChange={e => setContent(e.target.value)}
                />
                {lesson.reflection_realization_reference?.updated_at && (
                  <p className={styles.time}>
                    {format(
                      new Date(
                        lesson.reflection_realization_reference.updated_at,
                      ),
                      "yyyy/MM/dd HH:mm",
                    )}
                  </p>
                )}
                <button
                  className={clsx(
                    styles.saveButton,
                    isReflected && styles.isReflected,
                  )}
                  onClick={() =>
                    handleSubmitRealization(
                      lesson.id,
                      content,
                      lesson.reflection_realization_reference?.realization_id ||
                        null,
                    )
                  }
                  disabled={!content.trim() || calling}
                >
                  {isReflected ? "修正を保存する" : "保存する"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className={styles.noMessage}>{NO_RETROSPECTIVE_MESSAGE}</p>
        )}
      </div>
    </div>
  );
};

export default ActionAnswerForm;
